import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Sparkles, Zap, ArrowLeft, Loader2, Lightbulb } from 'lucide-react';

export default function AIProductGenerator() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [ideaLoading, setIdeaLoading] = useState(false);
  const [form, setForm] = useState({
    type: 'guide', seed_prompt: '', target_audience: '', content_direction: '',
    teaser_length_seconds: 90, full_length_minutes: 15, difficulty: 'beginner',
    tone: 'friendly', include_timestamps: true, include_checklist: true,
    include_examples: true, include_recap: true, landing_template_type: 'problem_solution',
    landing_angle: 'save time'
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const generateIdea = async () => {
    setIdeaLoading(true);
    try {
      const { data } = await api.post('/ai/generate-idea');
      setForm(prev => ({
        ...prev,
        seed_prompt: data.seed_prompt || prev.seed_prompt,
        type: data.type || prev.type,
        target_audience: data.target_audience || prev.target_audience,
        content_direction: data.content_direction || prev.content_direction,
      }));
      toast.success('New idea generated!');
    } catch {
      toast.error('Failed to generate idea');
    } finally { setIdeaLoading(false); }
  };

  const generate = async () => {
    if (!form.seed_prompt) { toast.error('Please enter a seed prompt'); return; }
    setGenerating(true);
    try {
      toast.info('Wayfinder is generating your product... This may take a minute.');
      const { data } = await api.post('/ai/generate-product', form);
      toast.success('Product generated successfully!');
      navigate(`/admin/products/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-6" data-testid="ai-product-generator">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/products')}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#E8735A]" />Generate with AI
          </h1>
          <p className="text-sm text-muted-foreground">Let Wayfinder create a complete audio product from your idea</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card className="border-l-4 border-l-[#1A9DA5]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Zap className="w-4 h-4 text-[#1A9DA5]" />Your Idea</CardTitle>
                <Button data-testid="generate-idea-btn" variant="outline" size="sm" onClick={generateIdea} disabled={ideaLoading} className="border-[#1A9DA5]/30 text-[#1A9DA5] hover:bg-[#1A9DA5]/10 hover:text-[#1A9DA5]">
                  {ideaLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lightbulb className="w-3.5 h-3.5 mr-1.5" />}
                  {ideaLoading ? 'Thinking...' : 'Generate New Idea'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label>Seed Prompt *</Label>
                <Textarea data-testid="seed-prompt" value={form.seed_prompt} onChange={e => update('seed_prompt', e.target.value)} rows={3} placeholder="e.g., Help local gyms reduce member churn in 14 days" className="focus-visible:ring-[#1A9DA5]" />
              </div>
              <div className="space-y-1">
                <Label>Product Type</Label>
                <Select value={form.type} onValueChange={v => update('type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guide">Guide</SelectItem>
                    <SelectItem value="package">Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Target Audience (optional)</Label><Input value={form.target_audience} onChange={e => update('target_audience', e.target.value)} placeholder="e.g., salon owners, personal trainers" /></div>
              <div className="space-y-1"><Label>Content Direction (optional)</Label><Textarea value={form.content_direction} onChange={e => update('content_direction', e.target.value)} rows={2} placeholder="e.g., Actionable tips, avoid fluff, focus on ROI" /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Length & Format</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Full Guide (minutes)</Label><Input type="number" value={form.full_length_minutes} onChange={e => update('full_length_minutes', parseInt(e.target.value) || 15)} /></div>
                <div className="space-y-1"><Label>Teaser (seconds)</Label><Input type="number" value={form.teaser_length_seconds} onChange={e => update('teaser_length_seconds', parseInt(e.target.value) || 90)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Difficulty</Label>
                  <Select value={form.difficulty} onValueChange={v => update('difficulty', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Tone</Label>
                  <Select value={form.tone} onValueChange={v => update('tone', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="playful">Playful</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Include Options</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[
                ['include_timestamps', 'Chapter Timestamps'],
                ['include_checklist', 'Action Checklist'],
                ['include_examples', 'Scripts & Templates'],
                ['include_recap', 'Recap Summary'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <Label className="cursor-pointer">{label}</Label>
                  <Switch checked={form[key]} onCheckedChange={v => update(key, v)} />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Landing Page</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label>Template</Label>
                <Select value={form.landing_template_type} onValueChange={v => update('landing_template_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="problem_solution">Problem/Solution</SelectItem>
                    <SelectItem value="case_study">Case Study</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                    <SelectItem value="webinar_style">Webinar Style</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Angle</Label>
                <Select value={form.landing_angle} onValueChange={v => update('landing_angle', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="save time">Save Time</SelectItem>
                    <SelectItem value="increase profit">Increase Profit</SelectItem>
                    <SelectItem value="reduce stress">Reduce Stress</SelectItem>
                    <SelectItem value="avoid mistakes">Avoid Mistakes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Button data-testid="generate-product-btn" onClick={generate} disabled={generating} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white py-6 text-lg font-semibold">
            {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Wayfinder is working...</> : <><Sparkles className="w-5 h-5 mr-2" />Generate Product</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
